import React, { useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
interface LightboxProps {
  images: string[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
}
export function Lightbox({
  images,
  currentIndex,
  isOpen,
  onClose,
  onNavigate
}: LightboxProps) {
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') {
      onNavigate(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
    }
    if (e.key === 'ArrowRight') {
      onNavigate(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
    }
  }, [isOpen, currentIndex, images.length, onClose, onNavigate]);
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-111" data-mp="1qbhcr9ni3qix">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[100] flex items-center justify-center bg-soft-dark/90 backdrop-blur-md" onClick={onClose} data-id="element-112" data-mp="1qc0uap98ocwf">
        
        {/* Close Button */}
        <button onClick={e => {
        e.stopPropagation();
        onClose();
      }} className="absolute top-6 right-6 p-2 text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10" aria-label="Close gallery" data-id="element-113" data-mp="2e1lefbh4pg7a">
          
          <X className="w-8 h-8" strokeWidth={1.5} data-id="element-114" data-mp="xkh6es1p6ao3" />
        </button>

        {/* Counter */}
        <div className="absolute top-8 left-8 text-white/70 font-light tracking-widest text-sm" data-id="element-115" data-mp="z3pl7isepeoo">
          {currentIndex + 1} / {images.length}
        </div>

        {/* Main Image Container */}
        <div className="relative w-full max-w-6xl max-h-[80vh] px-12 md:px-24 flex items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-116" data-mp="q2rid3tpfd7o">
          
          {/* Prev Button */}
          <button onClick={() => onNavigate(currentIndex === 0 ? images.length - 1 : currentIndex - 1)} className="absolute left-4 md:left-8 p-3 text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10" aria-label="Previous image" data-id="element-117" data-mp="gbwslph91vhh">
            
            <ChevronLeft className="w-8 h-8" strokeWidth={1.5} data-id="element-118" data-mp="bd3djsmbdwf7" />
          </button>

          {/* Image */}
          <motion.img key={currentIndex} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} src={images[currentIndex]} alt={`Gallery image ${currentIndex + 1}`} className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl" data-id="element-119" data-mp="3tiq95ifzy559" />
          

          {/* Next Button */}
          <button onClick={() => onNavigate(currentIndex === images.length - 1 ? 0 : currentIndex + 1)} className="absolute right-4 md:right-8 p-3 text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10" aria-label="Next image" data-id="element-120" data-mp="3jv4303sd49r0">
            
            <ChevronRight className="w-8 h-8" strokeWidth={1.5} data-id="element-121" data-mp="3svir1awpo2y7" />
          </button>
        </div>

        {/* Thumbnail Strip */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 px-6 overflow-x-auto pb-4" onClick={e => e.stopPropagation()} data-id="element-122" data-mp="6g7q0sw4a0dl">
          
          {images.map((img, idx) => <button key={idx} onClick={() => onNavigate(idx)} className={`relative shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all ${idx === currentIndex ? 'ring-2 ring-white ring-offset-2 ring-offset-soft-dark opacity-100' : 'opacity-50 hover:opacity-100'}`} data-id="element-123" data-mp="kdmkwd5530cy" data-mpi={idx}>
            
              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" data-id="element-124" data-mp="2eucsu226vnkz" />
            
            </button>)}
        </div>
      </motion.div>
    </AnimatePresence>;
}